import {getMap, PlanTrailActionContext} from "./types";
import {normalizeNotificationDistance} from "../../../utils/normalizeNotificationDistance";
import {GridRefUtils} from "../../../../../utils/helpers/GridRefPreference";
import {MeasurementPrefUtility} from "../../../../../utils/helpers/MeasurementPref";
import {MarkerFeature, MarkerGeometry, MarkerProperties} from "../../../../../utils/models/MarkerFeature";
import {generateID} from "../../../../../utils/helpers/IDGenerator";
import {getLatLonFromURLForPlannedTrail} from "../../../../../utils/helpers/GetLatLonFromURLForPlannedTrail";
import {IndexedDBCache} from "../../../../../utils/crosshair_altitude_service/indexed_db_cache";
import {getElevation} from "../../../../../utils/crosshair_altitude_service/pixel_methods";
import {throttle} from 'lodash-es';
import mapboxgl from "mapbox-gl";
import {ServiceProvider} from "../../../services/ServiceProvider";
import {LineStringFeature, LineStringGeometry, LineStringProperties, LineStringPropertiesExtra, LineStringPropertiesSurface, LineStringPropertiesWaytypes, LineStringPropertiesSummary} from "../../../../../utils/models/LineStringFeature";
import {bbox as turfBbox} from "@turf/turf";
import graphClient from "../../../../../utils/graphql/graph_client";
import { GetAllHikeTags, GetUserCustomTags } from "../../../../../utils/graphql/hike-tags-graph";
import { GetHike } from "../../../../../utils/graphql/hikes-graph";
import { GetRecordedTrailByRecordingId } from "../../../../../utils/graphql/recorded-trail-graph";
import { GetTrailData } from "../../../../../utils/graphql/trail-data-graph";
import { Route, RouteFactory } from "../../../../../utils/models/Route";
import { loadStateFromIndexedDB, migrateFromLocalStorage, clearStateFromIndexedDB } from "../../storeDB";
import { startSessionKeepAlive } from "../../../services/SessionKeepAliveService";

// Helper function to parse WKT LINESTRING format
function parseLineStringWKT(wkt: string, altitudes?: number[]): number[][] | null {
  const match = wkt.match(/LINESTRING\s*\((.*)\)/);
  if (!match) return null;

  const coordsString = match[1];
  const coordinateStrings = coordsString.split(',');
  const coordinateCount = coordinateStrings.length;

  let reportedAltitudeMismatch = false;

  const coordinates = coordinateStrings.map((coord, index) => {
    const parts = coord.trim().split(/\s+/);
    const longitude = parseFloat(parts[0]);
    const latitude = parseFloat(parts[1]);
    const result: number[] = [longitude, latitude];

    if (altitudes && altitudes.length > 0) {
      if (altitudes.length !== coordinateCount && !reportedAltitudeMismatch) {
        console.warn(
          `Altitude array length (${altitudes.length}) does not match coordinates length (${coordinateCount}) for WKT LINESTRING.`
        );
        reportedAltitudeMismatch = true;
      }

      const altitudeIndex = Math.min(index, altitudes.length - 1);
      const altitudeValue = altitudes[altitudeIndex];

      if (typeof altitudeValue === 'number' && Number.isFinite(altitudeValue)) {
        result.push(altitudeValue);
      }
    }

    return result;
  });

  return coordinates;
}

// Helper function to parse WKT POINT format
function parsePointWKT(wkt: string): [number, number] | null {
  const match = wkt.match(/POINT\s*\((.*)\)/);
  if (!match) return null;

  const parts = match[1].trim().split(' ');
  return [parseFloat(parts[0]), parseFloat(parts[1])];
}

function normalizeTagList(tagData: any): string[] {
  if (!tagData) return [];

  const collected: string[] = [];

  const process = (input: any) => {
    if (!input && input !== 0) {
      return;
    }

    if (Array.isArray(input)) {
      input.forEach(process);
      return;
    }

    if (typeof input === 'string') {
      collected.push(input);
      return;
    }

    if (typeof input === 'object') {
      if (input === null) {
        return;
      }

      if (typeof input.name === 'string') {
        collected.push(input.name);
      } else if (typeof input.value === 'string') {
        collected.push(input.value);
      }

      if (Object.prototype.hasOwnProperty.call(input, 'tag')) {
        process((input as any).tag);
      }

      if (Object.prototype.hasOwnProperty.call(input, 'node')) {
        process((input as any).node);
      }

      if (Object.prototype.hasOwnProperty.call(input, 'nodes')) {
        process((input as any).nodes);
      }

      if (Object.prototype.hasOwnProperty.call(input, 'edges')) {
        process((input as any).edges);
      }
    }
  };

  process(tagData);

  return Array.from(new Set(collected));
}

// Function to detect and load recording data
async function detectAndLoadRecording({ state, commit, dispatch }: PlanTrailActionContext): Promise<boolean> {
  console.log('🔍 detectAndLoadRecording - Checking for recording parameters');

  const urlParams = new URLSearchParams(window.location.search);
  const trailType = urlParams.get('trail_type');
  const trailId = urlParams.get('trail_id');

  console.log('📋 URL parameters:', {
    trail_type: trailType,
    trail_id: trailId,
    fullUrl: window.location.href
  });

  if (trailType === 'Recording' && trailId) {
    console.log('✅ Recording detected - trail_id:', trailId);

    try {
      console.log('📡 Making GraphQL request for recording ID:', trailId);
      const response = await graphClient.request(GetRecordedTrailByRecordingId, { id: trailId });
      console.log('✅ GraphQL response received:', response);

      const { recording } = response;
      if (!recording) {
        console.error('❌ Recording not found in response');
        return false;
      }

      console.log('📊 Recording data loaded:', {
        id: recording.id,
        title: recording.title,
        hasLineString: !!recording.lineString,
        lineStringLength: recording.lineString?.length
      });

      if (recording.lineString) {
        console.log('📐 Parsing GeoJSON from recording lineString');
        const geojson = JSON.parse(recording.lineString);
        console.log('🗺️ GeoJSON parsed:', {
          type: geojson?.type,
          featuresCount: geojson?.features?.length,
          coordinatesCount: geojson?.features?.[0]?.geometry?.coordinates?.length
        });

        // Create a route from the recording data
        const routeId = generateID();
        const route = RouteFactory.createNew(recording.title || 'Recording', 0, 1);

        // Process GeoJSON features
        const lineStringFeatures: LineStringFeature[] = [];
        const markerFeatures: MarkerFeature[] = [];

        if (geojson.features) {
          for (const feature of geojson.features) {
            if (feature.geometry.type === "LineString") {
              const lineStringFeature = LineStringFeature.fromGeoJsonForEdit(feature);
              lineStringFeatures.push(lineStringFeature);

              // Create markers from start and end points
              const coordinates = feature.geometry.coordinates;
              if (coordinates.length >= 2) {
                const startCoord = coordinates[0];
                const endCoord = coordinates[coordinates.length - 1];

                const startMarker = new MarkerFeature(
                  new MarkerGeometry([startCoord[0], startCoord[1]]),
                  new MarkerProperties(generateID(), true, Date.now().toString(), 0)
                );

                const endMarker = new MarkerFeature(
                  new MarkerGeometry([endCoord[0], endCoord[1]]),
                  new MarkerProperties(generateID(), true, Date.now().toString(), 0)
                );

                markerFeatures.push(startMarker, endMarker);
              }
            } else if (feature.geometry.type === "Point") {
              const markerFeature = MarkerFeature.fromGeoJson(feature);
              markerFeatures.push(markerFeature);
            }
          }
        }

        // Set up the route
        route.markers = markerFeatures;
        route.lineStrings = lineStringFeatures;
        route.route_style = 'route';

        // Clear existing routes and set the recording route
        state.routes = { [routeId]: route };
        state.activeRouteKey = routeId;
        state.title = recording.title || 'Recording';
        state.description = recording.description || '';
        state.clientId = generateID();
        state.public = true;

        console.log('✅ Recording route created:', {
          routeId,
          markersCount: markerFeatures.length,
          lineStringsCount: lineStringFeatures.length
        });

        // Set map bounds to show the recording route
        if (lineStringFeatures.length > 0) {
          console.log('🗺️ Setting map bounds for recording route');
          // Create a GeoJSON feature collection from the line strings
          const featureCollection: GeoJSON.FeatureCollection = {
            type: "FeatureCollection",
            features: lineStringFeatures.map(lineStringFeature => ({
              type: "Feature",
              geometry: lineStringFeature.lineStringGeometry,
              properties: {}
            }))
          };

          // Use the existing setMapBounds action
          dispatch('setMapBounds', { geoJson: featureCollection, duration: 1000 });
        }

        return true;
      } else {
        console.error('❌ Recording lineString not found');
        return false;
      }
    } catch (error) {
      console.error('❌ Error loading recording data:', error);
      return false;
    }
  }

  console.log('❌ No recording parameters detected');
  return false;
}

export const initializationActions = {
  async initialiseStore({ state, commit, dispatch }: PlanTrailActionContext) {
    console.log('initialiseStore called');

    // Start the session keep-alive service to prevent session expiration
    // during long planning sessions
    startSessionKeepAlive();

    try {
      const response = await graphClient.request(GetAllHikeTags);
      const allHikeTags = response?.allHikeTags;
      const featureTags = Array.isArray(allHikeTags?.featureTags) ? allHikeTags.featureTags : [];
      const activityTags = Array.isArray(allHikeTags?.activityTags) ? allHikeTags.activityTags : [];

      commit('setAvailableFeatureTags', featureTags);
      commit('setAvailableActivityTags', activityTags);
    } catch (error) {
      console.error('Failed to load hike tags', error);
      commit('setAvailableFeatureTags', []);
      commit('setAvailableActivityTags', []);
    }

    // Load the user's previously-created custom tags (their personal catalog).
    try {
      const customResponse = await graphClient.request(GetUserCustomTags);
      const customTags = Array.isArray(customResponse?.userCustomTags) ? customResponse.userCustomTags : [];
      commit('setAvailableCustomTags', customTags);
    } catch (error) {
      console.error('Failed to load user custom tags', error);
      commit('setAvailableCustomTags', []);
    }

    // Check if we're editing a hike
    if (window.hikeData) {
      const hikeData = window.hikeData;
      state.title = hikeData.name;
      state.description = hikeData.description;
      state.hikeId = hikeData.id;
      state.isHikeEdit = true;
      state.plannedToHikeOn = hikeData.planned_to_hike_at || null;
      
      // Load hike days if available
      if (hikeData.hike_days) {
        state.dayInfo = {};
        hikeData.hike_days.forEach((day: any) => {
          state.dayInfo[day.day_number] = {
            id: day.id,  // Store the hike day ID for updates
            name: day.name || '',
            description: day.description || ''
          };
        });
      }
      
      // Load hike highlights if available
      const generateHighlightImageClientId = () => {
        if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
          return crypto.randomUUID();
        }
        return `highlight_image_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      };

      const normaliseHighlightUploads = (uploads?: any[]) => {
        if (!uploads || uploads.length === 0) return undefined;

        return uploads
          .map((entry) => {
            if (!entry) return null;

            if (typeof entry === 'string') {
              return {
                signedId: entry,
                clientId: generateHighlightImageClientId()
              };
            }

            const signedId = entry.signedId || entry.signed_id;
            if (!signedId) return null;
            const clientId = entry.clientId || entry.client_id || generateHighlightImageClientId();
            return { signedId, clientId };
          })
          .filter(Boolean) as Array<{ signedId: string; clientId: string }>;
      };

      if (hikeData.hike_highlights) {
        state.hikeHighlights = hikeData.hike_highlights.map((highlight: any) => ({
          clientId: highlight.client_id || highlight.clientId || `highlight_${highlight.id}`,
          type: highlight.type,
          title: highlight.title || '',
          description: highlight.description || '',
          coordinates: highlight.coordinates || [0, 0],
          dayNumber: highlight.day_number || highlight.dayNumber,
          notificationDistance: normalizeNotificationDistance(highlight.notification_distance ?? highlight.notificationDistance),
          images: (highlight.images || []).map((image: any, index: number) => {
            if (!image) return null;
            const clientId = image.clientId || image.client_id || generateHighlightImageClientId();
            return {
              ...image,
              clientId,
              _destroy: Boolean(image._destroy)
            };
          }).filter(Boolean),
          imageSignedIds: normaliseHighlightUploads(highlight.imageSignedIds),
          imagePreviewUrls: highlight.imagePreviewUrls || [],
          media: (highlight.mediaAttachments || highlight.media_attachments || []).map((item: any) => {
            if (!item) return null;
            return {
              ...item,
              clientId: item.clientId || item.client_id || generateHighlightImageClientId(),
              mediaType: item.mediaType || item.media_type || 'audio',
              _destroy: false
            };
          }).filter(Boolean),
          mediaSignedIds: undefined
        }));
      }

      // Create a single route from the hike data
      state.routes = {};

      if (hikeData.hikes_planned_trails) {
        for (const [index, hikePlannedTrail] of hikeData.hikes_planned_trails.entries()) {
          const routeId = generateID();
          if (index === 0) {
            state.activeRouteKey = routeId;
          }

          const plannedTrail = hikePlannedTrail.ptv2_planned_trail
          const lineStrings = plannedTrail.line_strings.map((lineString: any) => {
            const wkt = lineString.geometry;
            const coordinates = parseLineStringWKT(wkt, lineString.altitudes);
            
            // Convert surface data from backend format to frontend format
            // Backend sends array of surface integers per coordinate
            // Frontend expects segments: [[startIdx, endIdx, surfaceType], ...]
            const surfaceValues = [];
            if (lineString.surfaces && lineString.surfaces.length > 0) {
              let currentSurface = lineString.surfaces[0];
              let segmentStart = 0;
              
              for (let i = 1; i <= lineString.surfaces.length; i++) {
                // Check if we've reached the end or found a different surface
                if (i === lineString.surfaces.length || lineString.surfaces[i] !== currentSurface) {
                  // Add the segment
                  surfaceValues.push([segmentStart, i, currentSurface]);
                  
                  // Start new segment if not at end
                  if (i < lineString.surfaces.length) {
                    currentSurface = lineString.surfaces[i];
                    segmentStart = i;
                  }
                }
              }
            }
            
            const waytypeValues = []; // Empty for now as the data doesn't include waytypes
            
            // Convert distances array to the expected format
            // Backend sends array of cumulative distances
            // Frontend expects segments: [[startIdx, endIdx, distance], ...]
            const distancesData = [];
            if (lineString.distances && lineString.distances.length > 0) {
              for (let i = 0; i < lineString.distances.length - 1; i++) {
                const distance = lineString.distances[i + 1] - lineString.distances[i];
                distancesData.push([i, i + 1, distance]);
              }
            }
            
            // Calculate ascent and descent from elevation data
            let ascent = 0;
            let descent = 0;
            if (lineString.altitudes && lineString.altitudes.length > 1) {
              for (let i = 1; i < lineString.altitudes.length; i++) {
                const elevDiff = lineString.altitudes[i] - lineString.altitudes[i - 1];
                if (elevDiff > 0) {
                  ascent += elevDiff;
                } else {
                  descent += Math.abs(elevDiff);
                }
              }
            }
            
            // Create the feature with full properties
            const lineStringFeature = new LineStringFeature(
              new LineStringGeometry(coordinates),
              new LineStringProperties(
                ascent,
                descent,
                [], // segments
                new LineStringPropertiesExtra(
                  new LineStringPropertiesSurface(surfaceValues, []),
                  new LineStringPropertiesWaytypes(waytypeValues, []),
                  distancesData
                ),
                new LineStringPropertiesSummary(
                  lineString.distances ? lineString.distances[lineString.distances.length - 1] : 0,
                  0 // duration - not provided in the data
                ),
                [] // wayPoints
              ),
              [] // bbox - will be calculated later if needed
            );
            
            // Set the ID
            lineStringFeature.lineStringProperties.id = lineString.id?.toString() || generateID();
            
            return lineStringFeature;
          });
          const markers = plannedTrail.markers.map((marker: any, index: number) => {
            const wkt = marker.lonlat;
            const coords = parsePointWKT(wkt);
            return new MarkerFeature(
              new MarkerGeometry([coords[0], coords[1]]),
              new MarkerProperties(
                marker.id?.toString() || generateID(),
                marker.snap !== false,
                new Date(marker.created_at).toString(),
                marker.elevation_meters
              )
            );
          })
          // Check if this PlannedTrail is used by multiple hikes (making it a reference)
          const referencedHikes = plannedTrail.hikes || [];
          const isReference = referencedHikes.length > 1;

          state.routes[routeId] = {
            id: routeId,
            name: plannedTrail.title || 'Route 1',
            markers: markers,
            lineStrings: lineStrings,
            undoMarkers: [],
            redoMarkers: [],
            undoLineStrings: [],
            redoLineStrings: [],
            created_at: new Date(plannedTrail.created_at),
            updated_at: new Date(plannedTrail.updated_at),
            plannedTrailId: plannedTrail.id,
            position: index,
            route_style: plannedTrail.route_style,
            day: hikePlannedTrail.day || 1,  // Add the day from hikes_planned_trails
            // Always include reference information (even if false)
            isReference: isReference,
            referencedHikes: isReference ? referencedHikes : []
          }
        }
      }
      state.clientId = generateID();
      state.public = hikeData.publicly_visible !== undefined ? hikeData.publicly_visible : true;

      const featureTags = normalizeTagList(
        hikeData.feature_tags || hikeData.feature_tag_list || hikeData.featureTags || hikeData.featureTagList
      );
      const activityTags = normalizeTagList(
        hikeData.activity_tags || hikeData.activity_tag_list || hikeData.activityTags || hikeData.activityTagList
      );
      const customTags = normalizeTagList(
        hikeData.custom_tags || hikeData.custom_tag_list || hikeData.customTags || hikeData.customTagList
      );

      commit('setSelectedFeatureTags', featureTags);
      commit('setSelectedActivityTags', activityTags);
      commit('setSelectedCustomTags', customTags);

      // Load existing hike images for display and updates
      state.photosIds = [];
      state.photosToUpload = [];
      state.photoSignedIds = {};
      state.photoExistingIds = {};
      state.photosMarkedForRemoval = [];
      state.existingHikeImages = [];

      try {
        const { hike: hikeWithImages } = await graphClient.request(GetHike, { id: hikeData.id });

        if (hikeWithImages?.images && Array.isArray(hikeWithImages.images)) {
          const loadedImages: Array<{ clientId: string; parsedId: number; previewUrl: string; isCover: boolean; signedId: string; file: any }> = [];

          hikeWithImages.images.forEach((image: any) => {
            const clientId = image.clientId || image.client_id || generateID();
            const parsedId = typeof image.id === 'string' ? parseInt(image.id, 10) : image.id;
            const previewUrl = image.file?.lgUrl || image.file?.mdUrl || image.file?.smUrl || image.file?.url || image.url;
            const signedId = image.file?.signedId;
            const isCover = image.isCover === true;

            loadedImages.push({ clientId, parsedId, previewUrl, isCover, signedId, file: image.file });
          });

          // Sort so the cover image is first, preserving original order otherwise
          loadedImages.sort((a, b) => (b.isCover ? 1 : 0) - (a.isCover ? 1 : 0));

          loadedImages.forEach(({ clientId, parsedId, previewUrl, isCover, signedId, file }) => {
            state.existingHikeImages.push({ id: parsedId, clientId, previewUrl, isCover, file });

            if (!state.photosIds.includes(clientId)) {
              state.photosIds.push(clientId);
            }
            if (parsedId) {
              state.photoExistingIds[clientId] = parsedId;
            }
            if (signedId) {
              state.photoSignedIds[clientId] = signedId;
            }
          });
        }
      } catch (error) {
        console.error('Failed to load hike images', error);
      }
    }

    // Check if we're copying from a source hike (not editing, creating new)
    if (window.sourceHikeData && !window.hikeData) {
      const hikeData = window.sourceHikeData;
      state.title = `Copy of ${hikeData.name}`;
      state.description = hikeData.description;
      state.hikeId = null; // Important: null so it creates a new hike
      state.isHikeEdit = false; // Important: false so it creates a new hike
      // Record the hike we're copying from so the new hike links back to it
      // (sent as originalHikeId on CreateHike → backend propagates comments to the original).
      state.originalHikeId = hikeData.id ?? null;
      state.plannedToHikeOn = null; // Don't copy the planned date

      // Load hike days if available
      if (hikeData.hike_days) {
        state.dayInfo = {};
        hikeData.hike_days.forEach((day: any) => {
          state.dayInfo[day.day_number] = {
            // Don't include 'id' - we want new day records
            name: day.name || '',
            description: day.description || ''
          };
        });
      }

      // Load hike highlights if available
      const generateHighlightImageClientId = () => {
        if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
          return crypto.randomUUID();
        }
        return `highlight_image_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      };

      const normaliseHighlightUploads = (uploads?: any[]) => {
        if (!uploads || uploads.length === 0) return undefined;

        return uploads
          .map((entry) => {
            if (!entry) return null;

            if (typeof entry === 'string') {
              return {
                signedId: entry,
                clientId: generateHighlightImageClientId()
              };
            }

            const signedId = entry.signedId || entry.signed_id;
            if (!signedId) return null;
            const clientId = entry.clientId || entry.client_id || generateHighlightImageClientId();
            return { signedId, clientId };
          })
          .filter(Boolean) as Array<{ signedId: string; clientId: string }>;
      };

      if (hikeData.hike_highlights) {
        state.hikeHighlights = hikeData.hike_highlights.map((highlight: any) => ({
          clientId: generateHighlightImageClientId(), // Generate new client IDs
          type: highlight.type,
          title: highlight.title || '',
          description: highlight.description || '',
          coordinates: highlight.coordinates || [0, 0],
          dayNumber: highlight.day_number || highlight.dayNumber,
          notificationDistance: normalizeNotificationDistance(highlight.notification_distance ?? highlight.notificationDistance),
          images: (highlight.images || []).map((image: any) => {
            if (!image) return null;
            const clientId = generateHighlightImageClientId();
            return {
              ...image,
              id: undefined, // Don't copy IDs - new images will be created
              clientId,
              _destroy: false
            };
          }).filter(Boolean),
          imageSignedIds: undefined, // Don't copy signed IDs
          imagePreviewUrls: highlight.imagePreviewUrls || []
        }));
      }

      // Create routes from the hike data
      state.routes = {};

      if (hikeData.hikes_planned_trails) {
        for (const [index, hikePlannedTrail] of hikeData.hikes_planned_trails.entries()) {
          const routeId = generateID();
          if (index === 0) {
            state.activeRouteKey = routeId;
          }

          const plannedTrail = hikePlannedTrail.ptv2_planned_trail
          const lineStrings = plannedTrail.line_strings.map((lineString: any) => {
            const wkt = lineString.geometry;
            const coordinates = parseLineStringWKT(wkt, lineString.altitudes);

            // Convert surface data from backend format to frontend format
            const surfaceValues = [];
            if (lineString.surfaces && lineString.surfaces.length > 0) {
              let currentSurface = lineString.surfaces[0];
              let segmentStart = 0;

              for (let i = 1; i <= lineString.surfaces.length; i++) {
                if (i === lineString.surfaces.length || lineString.surfaces[i] !== currentSurface) {
                  surfaceValues.push([segmentStart, i, currentSurface]);

                  if (i < lineString.surfaces.length) {
                    currentSurface = lineString.surfaces[i];
                    segmentStart = i;
                  }
                }
              }
            }

            const waytypeValues = [];

            // Convert distances array to the expected format
            const distancesData = [];
            if (lineString.distances && lineString.distances.length > 0) {
              for (let i = 0; i < lineString.distances.length - 1; i++) {
                const distance = lineString.distances[i + 1] - lineString.distances[i];
                distancesData.push([i, i + 1, distance]);
              }
            }

            // Calculate ascent and descent from elevation data
            let ascent = 0;
            let descent = 0;
            if (lineString.altitudes && lineString.altitudes.length > 1) {
              for (let i = 1; i < lineString.altitudes.length; i++) {
                const elevDiff = lineString.altitudes[i] - lineString.altitudes[i - 1];
                if (elevDiff > 0) {
                  ascent += elevDiff;
                } else {
                  descent += Math.abs(elevDiff);
                }
              }
            }

            // Create the feature with full properties
            const lineStringFeature = new LineStringFeature(
              new LineStringGeometry(coordinates),
              new LineStringProperties(
                ascent,
                descent,
                [],
                new LineStringPropertiesExtra(
                  new LineStringPropertiesSurface(surfaceValues, []),
                  new LineStringPropertiesWaytypes(waytypeValues, []),
                  distancesData
                ),
                new LineStringPropertiesSummary(
                  lineString.distances ? lineString.distances[lineString.distances.length - 1] : 0,
                  0
                ),
                []
              ),
              []
            );

            // Generate new ID for copied line string
            lineStringFeature.lineStringProperties.id = generateID();

            return lineStringFeature;
          });
          const markers = plannedTrail.markers.map((marker: any) => {
            const wkt = marker.lonlat;
            const coords = parsePointWKT(wkt);
            return new MarkerFeature(
              new MarkerGeometry([coords[0], coords[1]]),
              new MarkerProperties(
                generateID(), // Generate new ID
                marker.snap !== false,
                new Date().toString(), // Use current time
                marker.elevation_meters
              )
            );
          })
          state.routes[routeId] = {
            id: routeId,
            name: plannedTrail.title || 'Route 1',
            markers: markers,
            lineStrings: lineStrings,
            undoMarkers: [],
            redoMarkers: [],
            undoLineStrings: [],
            redoLineStrings: [],
            created_at: new Date(),
            updated_at: new Date(),
            plannedTrailId: undefined, // Don't copy planned trail ID
            position: index,
            route_style: plannedTrail.route_style,
            day: hikePlannedTrail.day || 1
          }
        }
      }
      state.clientId = generateID();
      state.public = hikeData.publicly_visible !== undefined ? hikeData.publicly_visible : true;

      const featureTags = normalizeTagList(
        hikeData.feature_tags || hikeData.feature_tag_list || hikeData.featureTags || hikeData.featureTagList
      );
      const activityTags = normalizeTagList(
        hikeData.activity_tags || hikeData.activity_tag_list || hikeData.activityTags || hikeData.activityTagList
      );
      const customTags = normalizeTagList(
        hikeData.custom_tags || hikeData.custom_tag_list || hikeData.customTags || hikeData.customTagList
      );

      commit('setSelectedFeatureTags', featureTags);
      commit('setSelectedActivityTags', activityTags);
      commit('setSelectedCustomTags', customTags);

      // Don't copy images - user can add their own
      state.photosIds = [];
      state.photosToUpload = [];
      state.photoSignedIds = {};
      state.photoExistingIds = {};
      state.photosMarkedForRemoval = [];
      state.existingHikeImages = [];
    }

    // Check if we're editing a recording
    const isEditingRecording = await detectAndLoadRecording({ state, commit, dispatch });
    if (isEditingRecording) {
      console.log('✅ Recording loaded successfully');
    }

    // Rest of your existing localStorage and lineString handling code...
    // Check for imported route data from localStorage (e.g., from "Edit in Hike Planner" button)
    const importedRouteData = localStorage.getItem('hikerPlannerImport');
    let processedImport = false;

    console.log('Checking for hikerPlannerImport in localStorage:', importedRouteData ? 'Found' : 'Not found');
    console.log('window.hikeData:', window.hikeData);

    if (importedRouteData && !window.hikeData) {
      console.log('Processing imported route data...');
      try {
        const importedData = JSON.parse(importedRouteData);
        console.log('Parsed imported data:', importedData);

        // Clear the localStorage after reading
        localStorage.removeItem('hikerPlannerImport');

        const routes: { [key: string]: Route } = {};
        let position = 0;

        // Helper function to create start and end markers from lineString coordinates
        const createMarkersFromCoordinates = (coordinates: number[][]): MarkerFeature[] => {
          if (coordinates.length < 2) return [];

          // Only create markers for start and end points.
          // NOTE: these must be real MarkerGeometry/MarkerProperties instances
          // (not plain objects). Otherwise markerGeometry.lon()/.lat() are
          // undefined and markerProperties.isSnap is undefined — which makes
          // extending the route throw inside the snap callback and fall back to
          // a straight, elevation-less line. isSnap:true treats the imported
          // endpoints as network nodes, matching the rest of the planner.
          const startCoord = coordinates[0];
          const endCoord = coordinates[coordinates.length - 1];

          const startMarker = new MarkerFeature(
            new MarkerGeometry([startCoord[0], startCoord[1]]),
            new MarkerProperties(generateID(), true, Date.now().toString(), startCoord[2] ?? 0)
          );

          const endMarker = new MarkerFeature(
            new MarkerGeometry([endCoord[0], endCoord[1]]),
            new MarkerProperties(generateID(), true, Date.now().toString(), endCoord[2] ?? 0)
          );

          return [startMarker, endMarker];
        };

        // Check if it's a FeatureCollection (new format with metadata)
        if (importedData.type === "FeatureCollection" && Array.isArray(importedData.features)) {
          console.log('Processing FeatureCollection with', importedData.features.length, 'features');

          // Load hike name if available
          if (importedData.hikeName) {
            state.title = importedData.hikeName;
            console.log('Loaded hikeName:', state.title);
          }

          // Load hikeDays if available (for day names/descriptions)
          if (importedData.hikeDays && Array.isArray(importedData.hikeDays)) {
            state.dayInfo = {};
            importedData.hikeDays.forEach((day: any) => {
              const dayNumber = day.dayNumber || day.day_number;
              if (dayNumber != null) {
                state.dayInfo[dayNumber] = {
                  name: day.name || '',
                  description: day.description || ''
                };
              }
            });
            console.log('Loaded hikeDays into dayInfo:', state.dayInfo);
          }

          // Load hike highlights if available
          if (importedData.hikeHighlights && Array.isArray(importedData.hikeHighlights)) {
            state.hikeHighlights = importedData.hikeHighlights.map((highlight: any) => ({
              clientId: `highlight_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
              type: highlight.type,
              title: highlight.title || '',
              description: highlight.description || '',
              coordinates: highlight.coordinates || [0, 0],
              dayNumber: highlight.dayNumber,
              notificationDistance: normalizeNotificationDistance(highlight.notificationDistance),
              images: (highlight.images || []).map((image: any) => ({
                ...image,
                clientId: image.clientId || `img_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
              })),
              imageSignedIds: undefined,
              imagePreviewUrls: [],
              media: (highlight.media || highlight.mediaAttachments || []).map((item: any) => ({
                ...item,
                clientId: item.clientId || `media_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
                mediaType: item.mediaType || item.media_type || 'audio'
              })),
              mediaSignedIds: highlight.mediaSignedIds || undefined
            }));
            console.log('Loaded hikeHighlights:', state.hikeHighlights.length);
          }

          // Group features by day
          const featuresByDay: { [day: number]: any[] } = {};

          importedData.features.forEach((feature: any) => {
            const dayNumber = feature.properties?.hikeDayNumber || 1;
            if (!featuresByDay[dayNumber]) {
              featuresByDay[dayNumber] = [];
            }
            featuresByDay[dayNumber].push(feature);
          });

          // Sort days
          const sortedDays = Object.keys(featuresByDay).map(Number).sort((a, b) => a - b);
          console.log('Found features for days:', sortedDays);

          // Create routes for each feature, maintaining day information
          sortedDays.forEach(dayNumber => {
            featuresByDay[dayNumber].forEach((feature: any, indexInDay: number) => {
              const lineStringFeature = LineStringFeature.fromGeoJsonForEdit(feature);
              const markers = createMarkersFromCoordinates(feature.geometry.coordinates);
              const routeStyle = feature.properties?.routeStyle || 'route';
              const name = feature.properties?.name || `Route ${position + 1}`;
              const isReference = feature.properties?.isReference || false;
              const referencedHikes = feature.properties?.referencedHikes || [];
              const ptv2PlannedTrailId = feature.properties?.ptv2PlannedTrailId;

              const route = RouteFactory.createNew(name, position, dayNumber);
              route.markers = markers;
              route.lineStrings = [lineStringFeature];
              route.route_style = routeStyle as 'route' | 'alt' | 'spur' | 'transport';

              // Set reference properties if this is a reference route
              if (isReference && ptv2PlannedTrailId) {
                route.plannedTrailId = Number(ptv2PlannedTrailId);
                route.isReference = true;
                route.referencedHikes = referencedHikes;
              }

              routes[route.id] = route;

              console.log('Created route:', route.id, 'name:', name, 'day:', dayNumber, 'style:', routeStyle, 'isReference:', isReference, 'position:', position);
              position++;
            });
          });
        }
        // Fallback: Handle old format (LineString or MultiLineString without metadata)
        else if (importedData.type === "LineString") {
          console.log('Processing single LineString (legacy format)');
          const feature = {
            type: "Feature",
            geometry: importedData,
            properties: {}
          };
          const lineStringFeature = LineStringFeature.fromGeoJsonForEdit(feature);
          const markers = createMarkersFromCoordinates(importedData.coordinates);

          const route = RouteFactory.createNew(`Route 1`, position, 1);
          route.markers = markers;
          route.lineStrings = [lineStringFeature];
          routes[route.id] = route;
          console.log('Created route:', route.id, 'with', markers.length, 'markers');
        } else if (importedData.type === "MultiLineString") {
          console.log('Processing MultiLineString (legacy format), count:', importedData.coordinates.length);
          for (const coordinates of importedData.coordinates) {
            const feature = {
              type: "Feature",
              geometry: {
                type: "LineString",
                coordinates: coordinates
              },
              properties: {}
            };
            const lineStringFeature = LineStringFeature.fromGeoJsonForEdit(feature);
            const markers = createMarkersFromCoordinates(coordinates);

            const route = RouteFactory.createNew(`Route ${position + 1}`, position, 1);
            route.markers = markers;
            route.lineStrings = [lineStringFeature];
            routes[route.id] = route;
            console.log('Created route:', route.id, 'position:', position, 'with', markers.length, 'markers');
            position++;
          }
        }

        console.log('Total routes created:', Object.keys(routes).length);

        if (Object.keys(routes).length > 0) {
          // Set the routes
          state.routes = routes;
          // Set the first route as active
          const firstRouteId = Object.keys(routes)[0];
          state.activeRouteKey = firstRouteId;
          processedImport = true;

          console.log('Routes set in state, active route:', firstRouteId);
          console.log('State routes:', state.routes);

          // Routes will be added to the map by the existing setTimeout in PlanTrail.vue (line 678-680)
        }
      } catch (error) {
        console.error('Failed to import route data from localStorage:', error);
        localStorage.removeItem('hikerPlannerImport');
      }
    } else {
      console.log('Not processing import - importedRouteData:', !!importedRouteData, 'window.hikeData:', !!window.hikeData);
    }

    if (!processedImport && window.lineString && !window.hikeData) {
      // Its an edit
      const lineString = window.lineString;
      let markerFeatures: MarkerFeature[] = [];
      let lineStringFeatures: LineStringFeature[] = [];

      for (const feature of lineString.features) {
        if (feature.geometry.type === "LineString") {
          lineStringFeatures.push(LineStringFeature.fromGeoJsonForEdit(feature));
        } else if (feature.geometry.type === "Point") {
          markerFeatures.push(MarkerFeature.fromGeoJson(feature));
        }
      }

      commit("setMarkers", markerFeatures);
      commit("setLineStrings", lineStringFeatures);
    } else if (!processedImport && !state.isHikeEdit) {
      // First, attempt migration from localStorage to IndexedDB
      await migrateFromLocalStorage();

      // Then load from IndexedDB
      const storedState = await loadStateFromIndexedDB();
      if (storedState) {
        try {
          const planTrailData = storedState.planTrail;

          // Check if this is new routes format
          if (planTrailData.routes && Object.keys(planTrailData.routes).length > 0) {
            // New format with routes
            const hasData = Object.values(planTrailData.routes).some((route: any) =>
              route.markers && route.markers.length > 0
            );

            if (hasData) {
              const confirm = window.confirm('You have unsaved changes. Do you want to load them?');
              if (confirm) {
                // Restore routes with default route_style if missing
                state.routes = {};
                Object.keys(planTrailData.routes).forEach(key => {
                  const route = planTrailData.routes[key];
                  state.routes[key] = {
                    ...route,
                    route_style: route.route_style || 'route' // Default to 'route' if missing
                  };
                });
                state.activeRouteKey = planTrailData.activeRouteKey;

                // Restore other state
                state.isSnap = planTrailData.isSnap || true;
                state.clientId = planTrailData.clientId || generateID();
                state.routePreference = planTrailData.routePreference || 'hike';
                state.title = planTrailData.title || null;
                state.description = planTrailData.description || null;
                state.public = planTrailData.public !== undefined ? planTrailData.public : true;

                if (planTrailData.selectedFeatureTags) {
                  commit('setSelectedFeatureTags', normalizeTagList(planTrailData.selectedFeatureTags));
                }

                if (planTrailData.selectedActivityTags) {
                  commit('setSelectedActivityTags', normalizeTagList(planTrailData.selectedActivityTags));
                }

                if (planTrailData.selectedCustomTags) {
                  commit('setSelectedCustomTags', normalizeTagList(planTrailData.selectedCustomTags));
                }

                // Restore hike highlights
                if (planTrailData.hikeHighlights && planTrailData.hikeHighlights.length > 0) {
                  state.hikeHighlights = planTrailData.hikeHighlights;
                }
              } else {
                await clearStateFromIndexedDB();
              }
            }
          } else if (planTrailData.markers && planTrailData.markers.length > 0 &&
            planTrailData.lineStrings && planTrailData.lineStrings.length > 0) {
            // Old format - convert to new format
            const confirm = window.confirm('You have unsaved changes from an older version. Do you want to load them?');
            if (confirm) {
              // Create a single route from old data
              const routeId = generateID();
              state.routes = {
                [routeId]: {
                  id: routeId,
                  name: 'Route 1',
                  markers: planTrailData.markers,
                  lineStrings: planTrailData.lineStrings,
                  undoMarkers: [],
                  redoMarkers: [],
                  undoLineStrings: [],
                  redoLineStrings: [],
                  created_at: new Date(),
                  updated_at: new Date(),
                  route_style: 'route' // Default to 'route'
                }
              };
              state.activeRouteKey = routeId;
              state.isSnap = planTrailData.isSnap || true;
              state.clientId = planTrailData.clientId || generateID();
              state.routePreference = planTrailData.routePreference || 'hike';

              // Restore hike highlights for old format too
              if (planTrailData.hikeHighlights && planTrailData.hikeHighlights.length > 0) {
                state.hikeHighlights = planTrailData.hikeHighlights;
              }

              if (planTrailData.selectedFeatureTags) {
                commit('setSelectedFeatureTags', normalizeTagList(planTrailData.selectedFeatureTags));
              }

              if (planTrailData.selectedActivityTags) {
                commit('setSelectedActivityTags', normalizeTagList(planTrailData.selectedActivityTags));
              }

              if (planTrailData.selectedCustomTags) {
                commit('setSelectedCustomTags', normalizeTagList(planTrailData.selectedCustomTags));
              }

              await commit('disableRemovalMode');
            } else {
              await clearStateFromIndexedDB();
            }
          }
        } catch (error) {
          // If there's any error parsing or handling the stored data, just remove it
          console.error('Error loading stored state:', error);
          await clearStateFromIndexedDB();
        }
      }
    } else {
      // Editing an existing hike should not prompt to restore stale drafts
      await clearStateFromIndexedDB();
    }
  },

  setMapLatLonFromParams() {
    const params = new URLSearchParams(window.location.search);
    if (params.has('lat') && params.has('lng') && params.has('zoom')) {
      // Set map to fly to location
      const lat = parseFloat(params.get('lat') as string);
      const lng = parseFloat(params.get('lng') as string);
      const zoom = parseFloat(params.get('zoom') as string);
      const map = getMap();
      map.flyTo({
        center: [lng, lat],
        zoom: zoom,
        duration: 500
      });
    }
  },

  async mapInitialised({ state, dispatch, commit, getters }: PlanTrailActionContext) {
    const gridRef = GridRefUtils.preference();
    const measurementPreference = MeasurementPrefUtility.preference();
    const map = getMap();

    // When editing a hike, set bounds to show all routes
    if (state.isHikeEdit && Object.keys(state.routes).length > 0) {
      const allFeatures: any[] = [];

      // Collect all markers and lineStrings from all routes
      Object.values(state.routes).forEach((route: any) => {
        if (route.markers && route.markers.length > 0) {
          route.markers.forEach((marker: any) => {
            allFeatures.push(marker.toGeoJson());
          });
        }

        if (route.lineStrings && route.lineStrings.length > 0) {
          route.lineStrings.forEach((lineString: any) => {
            allFeatures.push(lineString.toGeoJson());
          });
        }
      });

      if (allFeatures.length > 0) {
        const geoJson = {
          type: 'FeatureCollection' as const,
          features: allFeatures
        };
        dispatch('setMapBounds', { geoJson, duration: 1000 });
      }
    } else {
      // Original logic for new trails or single-route editing
      const geoJson = getters['markersFeatureCollection'];
      if (geoJson.features.length > 0) {
        dispatch('setMapBounds', { geoJson });
      }
    }

    const cache = new IndexedDBCache();

    const handleMouseMove = async (e: mapboxgl.MapMouseEvent) => {
      const coords = gridRef.convert(e.lngLat);
      const elevation = await getElevation(e.lngLat, cache);
      commit('setMiniCoordsBar', `${coords.toString()} ${elevation}`);
    };

    const throttledMouseMove = throttle(handleMouseMove, 20); // Adjust the throttle interval as needed
    map.on('mousemove', throttledMouseMove);

    // Initialize services for refactored architecture
    if (window.map && window.layerRegistry) {
      const services = ServiceProvider.getInstance().initializeProduction(window.map, window.layerRegistry);

      // Set up event listeners for the refactored system
      services.eventBus.onMarkerDragEnd(async (event) => {
        // NOTE: We do NOT call updateUndo here because updateLineStringsFromMarkerMove
        // creates provisional lines that will be replaced by GraphHopper responses.
        // The undo state will be saved in replaceProvisionalLine once the real route arrives.
        // This prevents duplicate undo states and avoids saving provisional straight lines.

        // Update marker in state
        const marker = event.marker;
        marker.markerGeometry.coordinates = event.newPosition;
        marker.markerProperties.isSnap = state.isSnap;

        await commit('updateMarker', marker);

        // Find marker index and update line strings
        const index = state.markers.findIndex(m => m.markerProperties.id === marker.markerProperties.id);
        await dispatch('updateLineStringsFromMarkerMove', index);
      });

      services.eventBus.onLineClick(async (event) => {
        if (state.hoverPoint) {
          // Insert marker at hover point
          await dispatch('insertMarkerAtPoint', {
            point: [event.clickPoint[0], event.clickPoint[1]],
            routeId: event.routeId,
            insertIndex: event.insertIndex
          });
        }
      });
    }

    const latLonForPlannedTrail = getLatLonFromURLForPlannedTrail(window.location.href);
    if (latLonForPlannedTrail) {
      await dispatch('clearMap', true);

      // Create a new route if none exists
      if (!state.activeRouteKey || !state.routes[state.activeRouteKey]) {
        commit('createNewRoute', 'Route');
      }

      // Create marker using the multi-route approach
      const id = generateID();
      const marker = new MarkerFeature(
        new MarkerGeometry([latLonForPlannedTrail.lon, latLonForPlannedTrail.lat]),
        new MarkerProperties(id, true, Date.now().toString(), 0)
      );

      // Add marker to the active route
      const activeRoute = state.routes[state.activeRouteKey];
      activeRoute.markers.push(marker);
      activeRoute.updated_at = new Date();

      // Render all routes on the map (which includes our new marker)
      await dispatch('addAllRoutesToMap');

      map.flyTo({
        center: [latLonForPlannedTrail.lon, latLonForPlannedTrail.lat],
        zoom: 11,
        duration: 2500
      });
    }
  },

  setMapBounds({ }: PlanTrailActionContext, data: { geoJson: GeoJSON.FeatureCollection, duration?: number }) {
    const map = getMap();
    const newBbox = turfBbox(data.geoJson);
    // @ts-ignore
    const bounds = mapboxgl.LngLatBounds.convert(newBbox);
    map.fitBounds(bounds, {
      padding: 200,
      duration: data.duration != null ? data.duration : 0,
    });
  },

  /**
   * Load trail data from URL param and display as tracer route
   * Only accessible to trails@hiiker.co account
   */
  async loadTrailDataFromParam({ state, dispatch }: PlanTrailActionContext): Promise<boolean> {
    const TRAILS_USER_EMAIL = 'trails@hiiker.co';

    // Check for trailData URL param
    const urlParams = new URLSearchParams(window.location.search);
    const trailDataId = urlParams.get('trailData');

    if (!trailDataId) {
      return false;
    }

    // Verify user is trails@hiiker.co
    const currentUserEmail = (window as any).currentUserEmail;
    if (currentUserEmail !== TRAILS_USER_EMAIL) {
      console.warn('TrailData param is only accessible to the official trails account');
      return false;
    }

    try {
      console.log('📡 Fetching TrailData:', trailDataId);
      const response = await graphClient.request(GetTrailData, { id: trailDataId });

      if (!response?.trailData) {
        console.error('TrailData not found:', trailDataId);
        return false;
      }

      const { trailData } = response;
      console.log('✅ TrailData loaded:', trailData.name);

      // Store the trailDataId for later association when saving
      state.trailDataId = parseInt(trailDataId, 10);

      // Add GPX as tracer route if available
      if (trailData.gpxFile) {
        await dispatch('addTracerRoute', trailData.gpxFile);
        console.log('✅ TrailData GPX added as tracer route');

        // Set title from trail data if not already set
        if (!state.title) {
          state.title = trailData.name;
        }
      }

      return true;
    } catch (error) {
      console.error('Error loading TrailData:', error);
      return false;
    }
  }
};
